import { countryCodeToCountryName } from "@/utils/countries.util";
import type { CompanyEntryAddressDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryAddressDto";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import type { OfficeSelectOptionDto } from "@advanced-sales-technologies/backend-client/models/OfficeSelectOptionDto";
import { SelectOptionRequestDto } from "@advanced-sales-technologies/backend-client/models/SelectOptionRequestDto";
import { ColumnDef } from "@tanstack/react-table";
import clsx from "clsx";
import { HiOutlineMapPin } from "react-icons/hi2";

import { useGetOfficeOptions } from "@/hooks/api/list/options/useGetOfficeOptions";
import BaseColumn from "@/components/molecules/table/BaseColumn";
import HasMoreDataCellGeneral from "@/components/molecules/table/HasMoreDataCellGeneral";
import { searchColumns, SearchColumnsProps } from "@/components/organisms/search/searchColumns";

const officeAddressColumn = ({ t, listId, searchParams, queryKey }: SearchColumnsProps): ColumnDef<CompanyEntryDto> =>
  BaseColumn<CompanyEntryDto>({
    accessorFn: (row) => {
      const office = row.office;

      if (!office || !office.address) return null;

      const { city, country_code } = office.address;
      const countryName = countryCodeToCountryName(country_code);

      const formattedAddress = [city, countryName].filter((value) => value && value.trim()).join(", ");

      return formattedAddress || null;
    },
    id: searchColumns(t).office_address.id,
    size: 300,
    header: () => searchColumns(t).office_address.name,
    cell: ({ row }) => {
      const company = row.original;
      const { id: companyId } = company;
      const office = company.office;

      if (!office || !office.address) return null;

      const formattedAddressEntry = ({ city, country_code }: CompanyEntryAddressDto) => {
        const countryName = countryCodeToCountryName(country_code);

        return [city, countryName].filter((value) => value && value.trim()).join(", ");
      };

      const formattedAddressOption = ({ city, country_code }: OfficeSelectOptionDto) => {
        const countryName = countryCodeToCountryName(country_code);

        return [city, countryName].filter((value) => value && value.trim()).join(", ");
      };

      return (
        <HasMoreDataCellGeneral<OfficeSelectOptionDto>
          queryKey={queryKey}
          company={company}
          buildDisplayValue={formattedAddressOption}
          searchParams={searchParams}
          displayValue={formattedAddressEntry(office.address)}
          initialSelected={office?.id}
          listId={listId}
          target={SelectOptionRequestDto.target.OFFICE}
          hasMoreData={company.has_more_offices}
          placeholder={searchColumns(t).office_address.name}
          useQueryHook={(query, enabled, searchParams) =>
            useGetOfficeOptions(
              {
                companyId: companyId,
                option: {
                  query: query,
                  list_id: listId,
                  configuration: searchParams,
                },
              },
              enabled,
            )
          }
          renderOption={(option, isSelected) => (
            <span
              key={option.id}
              className={"flex flex-1 flex-col gap-x-1.5 truncate py-2 text-left text-sm text-quaternary"}
            >
              {option.street && (
                <div className={clsx("truncate font-medium", isSelected ? "text-primary" : "text-secondary")}>
                  {option.street}
                </div>
              )}
              <div className={"truncate"}>
                {option.zipcode} {option.city}
                {option.country_code && `; ${countryCodeToCountryName(option.country_code)}`}
              </div>
            </span>
          )}
        />
      );
    },
    meta: {
      heading: {
        icon: HiOutlineMapPin,
        className: "!bg-var-success-100 hover:!bg-var-success-200 active:!bg-var-success-300",
      },
      column: {
        className: "bg-var-success-50",
      },
    },
  });

export default officeAddressColumn;
